import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useRadarDisplay } from './RadarDisplay';

type Props<T extends string | string[]> = {
  value: T; onChange: (value: T) => void; singleLine?: boolean;
  'aria-label'?: string; required?: boolean; maxLength?: number; disabled?: boolean;
};

export function RadarLocalizedField<T extends string | string[]>({ value, onChange, singleLine, ...props }: Props<T>) {
  const { d } = useRadarDisplay();
  const source: string[] = typeof value === 'string' ? [value] : value;
  const translated = source.map(d);
  const [editing, setEditing] = useState<string | null>(null);
  const originalAtFocus = useRef(source);
  const displayAtFocus = useRef(translated);
  const sourceKey = JSON.stringify(value);
  const lastEmitted = useRef(sourceKey);
  useEffect(() => {
    if (sourceKey !== lastEmitted.current) {
      setEditing(null);
      originalAtFocus.current = source;
      displayAtFocus.current = translated;
      lastEmitted.current = sourceKey;
    }
  }, [sourceKey]);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const displayed = editing ?? translated.join('\n');
  useEffect(() => {
    const field = textarea.current;
    if (!field) return;
    const resize = () => { field.style.height = 'auto'; field.style.height = field.scrollHeight + 2 + 'px'; };
    resize();
    let width = field.clientWidth;
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => {
      if (width !== field.clientWidth) { width = field.clientWidth; resize(); }
    });
    observer?.observe(field);
    return () => observer?.disconnect();
  }, [displayed]);
  const handlers = {
    value: displayed,
    onFocus: () => { originalAtFocus.current = source; displayAtFocus.current = translated; },
    onBlur: () => setEditing(null),
    onCompositionStart: () => {
      if (editing === null) {
        originalAtFocus.current = source;
        displayAtFocus.current = translated;
        setEditing(displayed);
      }
    },
    onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      // Focus alone is not an edit. A locale/translation can change while the
      // untouched field is focused; snapshot what the user actually starts editing.
      if (editing === null) {
        originalAtFocus.current = source;
        displayAtFocus.current = translated;
      }
      const input = event.target.value;
      setEditing(input);
      // Unedited translated lines must never be saved as changed research codes.
      const values = input.split('\n').map((line, i) => {
        if (displayAtFocus.current[i] === line) return originalAtFocus.current[i];
        const previous = displayAtFocus.current.indexOf(line);
        return previous < 0 ? line : originalAtFocus.current[previous];
      });
      const next = (Array.isArray(value) ? values : values.join('\n')) as T;
      lastEmitted.current = JSON.stringify(next);
      onChange(next);
    },
  };
  return singleLine ? <input {...props} {...handlers}/> : <textarea ref={textarea} rows={2} className="radar-growing-field" {...props} {...handlers}/>;
}
